import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CtaSection from '../../components/feature/CtaSection';
import { useSEO, generateWebPageSchema } from '../../utils/seo';
import { Link } from 'react-router-dom';

export default function Recruit() {
  useSEO({
    title: '採用情報｜おかやま小児科クリニック｜岡山市北区',
    description:
      '岡山市北区のおかやま小児科クリニックの採用情報。看護師・医療事務スタッフ募集中。アットホームな職場環境で地域医療に貢献。正社員・パート募集。',
    keywords:
      '岡山市,小児科,採用情報,求人,看護師,医療事務,スタッフ募集,北区',
    canonicalPath: '/recruit',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '採用情報',
          'スタッフ採用情報・求人募集',
          '/recruit'
        ),
        {
          '@type': 'JobPosting',
          title: '看護師（正社員・パート）',
          description: '岡山市北区の小児科クリニックで看護師を募集。経験・能力により優遇。',
          hiringOrganization: {
            '@type': 'MedicalClinic',
            name: 'おかやま小児科クリニック',
            sameAs: import.meta.env.VITE_SITE_URL || '',
          },
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '○○町1-2-3',
              addressLocality: '岡山市北区',
              addressRegion: '岡山県',
              addressCountry: 'JP',
            },
          },
          employmentType: ['FULL_TIME', 'PART_TIME'],
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: 'JPY',
            value: {
              '@type': 'QuantitativeValue',
              minValue: 250000,
              maxValue: 350000,
              unitText: 'MONTH',
            },
          },
        },
        {
          '@type': 'JobPosting',
          title: '医療事務（正社員・パート）',
          description: '岡山市北区の小児科クリニックで医療事務を募集。未経験者歓迎、研修制度あり。',
          hiringOrganization: {
            '@type': 'MedicalClinic',
            name: 'おかやま小児科クリニック',
            sameAs: import.meta.env.VITE_SITE_URL || '',
          },
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '○○町1-2-3',
              addressLocality: '岡山市北区',
              addressRegion: '岡山県',
              addressCountry: 'JP',
            },
          },
          employmentType: ['FULL_TIME', 'PART_TIME'],
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: 'JPY',
            value: {
              '@type': 'QuantitativeValue',
              minValue: 200000,
              maxValue: 280000,
              unitText: 'MONTH',
            },
          },
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[25vh] sm:h-[30vh] flex items-center justify-center overflow-hidden mt-20 bg-gradient-to-r from-[#F48FB1] via-[#D4A0D0] to-[#A78BFA]">
        <div className="relative z-10 text-center text-white px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            採用情報
          </h2>
          <p className="text-base sm:text-lg md:text-xl">一緒に働く仲間を募集しています</p>
        </div>
      </section>

      {/* Recruit Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">

          <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl p-8 sm:p-12 mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <h4 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                  岡山市の子どもたちの健康を<br />
                  一緒に守りませんか？
                </h4>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                  おかやま小児科クリニックでは、お子様とご家族に寄り添う医療を提供するため、看護師・医療事務スタッフを募集しております。
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                  アットホームな雰囲気の中で、スタッフ一人ひとりが活躍できる職場環境を整えています。
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  岡山市で地域医療に貢献したい方、小児医療に興味のある方のご応募をお待ちしております。
                </p>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-lg">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/40081246724743dea99d2bafd61119af.webp"
                  alt="スタッフ募集"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Job Positions */}
          <div className="space-y-6 sm:space-y-8">
            {/* Position 1 */}
            <div className="bg-white border-2 border-[#FCE4EC] rounded-3xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-all">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h5 className="text-lg sm:text-xl font-bold text-gray-800">
                  看護師（正社員・パート）
                </h5>
                <span className="inline-block bg-[#F48FB1] text-white px-4 sm:px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap w-fit">
                  募集中
                </span>
              </div>
              <div className="space-y-4 text-sm sm:text-base text-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">勤務地</div>
                  <div className="sm:col-span-2">岡山市北区○○町1-2-3</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">勤務時間</div>
                  <div className="sm:col-span-2">
                    9:00〜18:00（休憩1時間）<br />
                    ※パートの方は応相談
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">給与</div>
                  <div className="sm:col-span-2">
                    経験・能力により優遇<br />
                    正社員：月給25万円〜35万円<br />
                    パート：時給1,500円〜2,000円
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">休日</div>
                  <div className="sm:col-span-2">日曜日・祝日、夏季休暇、年末年始</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">福利厚生</div>
                  <div className="sm:col-span-2">
                    社会保険完備、交通費支給、制服貸与、研修制度あり
                  </div>
                </div>
              </div>
            </div>

            {/* Position 2 */}
            <div className="bg-white border-2 border-[#EDE7F6] rounded-3xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-all">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h5 className="text-lg sm:text-xl font-bold text-gray-800">
                  医療事務（正社員・パート）
                </h5>
                <span className="inline-block bg-[#A78BFA] text-white px-4 sm:px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap w-fit">
                  募集中
                </span>
              </div>
              <div className="space-y-4 text-sm sm:text-base text-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">勤務地</div>
                  <div className="sm:col-span-2">岡山市北区○○町1-2-3</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">勤務時間</div>
                  <div className="sm:col-span-2">
                    8:30〜18:00（休憩1時間）<br />
                    ※パートの方は応相談
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">給与</div>
                  <div className="sm:col-span-2">
                    経験・能力により優遇<br />
                    正社員：月給20万円〜28万円<br />
                    パート：時給1,100円〜1,500円
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">休日</div>
                  <div className="sm:col-span-2">日曜日・祝日、夏季休暇、年末年始</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="font-bold">福利厚生</div>
                  <div className="sm:col-span-2">
                    社会保険完備、交通費支給、制服貸与、資格取得支援
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Staff Voices */}
          <div className="mt-16 sm:mt-24">
            <div className="text-center mb-12 sm:mb-16">
              <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-4">スタッフの声</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl p-6 sm:p-8 shadow-lg">
                <div className="flex items-start gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src="https://public.readdy.ai/ai/img_res/a37a0996ed5f281ff4421342ec652c8a.jpg"
                      alt="看護師スタッフ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-base sm:text-lg">看護師 3年目</p>
                    <p className="text-sm sm:text-base text-gray-600">Aさん（20代女性）</p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  子どもが好きで小児科を選びました。院長先生やスタッフの皆さんが優しく、働きやすい環境です。お子様の笑顔を見ると、この仕事を選んで良かったと実感します。岡山市の地域医療に貢献できることにやりがいを感じています。
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#EDE7F6]/40 to-[#FCE4EC]/30 rounded-3xl p-6 sm:p-8 shadow-lg">
                <div className="flex items-start gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20Japanese%20female%20medical%20receptionist%20in%20professional%20attire%20smiling%20kindly%20standing%20at%20modern%20clinic%20reception%20desk%20natural%20lighting%20high%20quality%20professional%20headshot%20photography%20welcoming%20and%20professional%20demeanor&width=400&height=400&seq=facilities008&orientation=squarish"
                      alt="医療事務スタッフ"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-base sm:text-lg">医療事務 5年目</p>
                    <p className="text-sm sm:text-base text-gray-600">Bさん（30代女性）</p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  未経験からスタートしましたが、先輩方が丁寧に教えてくださり、今では自信を持って業務に取り組めています。患者様やご家族から「ありがとう」と言っていただけることが何よりの励みです。子育てとの両立もしやすい職場です。
                </p>
              </div>
            </div>
          </div>

          {/* Application - replaced with CTA */}
        </div>
      </section>

      {/* CTA Section - Customized for Recruit */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#F48FB1] to-[#EC407A]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            子どもたちの健康を一緒に守りませんか
          </h3>
          <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-12 leading-relaxed px-4">
            岡山市で子どもたちの笑顔を支えるチーム
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-white hover:bg-gray-50 text-[#F48FB1] px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              お問い合わせ
            </Link>
            <a
              href="tel:0860123456"
              className="w-full sm:w-auto bg-[#D81B60] hover:bg-[#C2185B] text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line text-xl sm:text-2xl"></i>
              086-012-3456
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
